import BaseComponent from 'foundation/base-component';
import PropTypes from 'prop-types';
import React from 'react';
import reactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import imageCompression from 'browser-image-compression';

const toBoolean = (value) => value === true || value === 'true';

const getObjectUrlApi = () => window.URL || window.webkitURL;

const revokePreviewUrl = (previewUrl) => {
    const objectUrlApi = getObjectUrlApi();

    if (previewUrl && objectUrlApi && objectUrlApi.revokeObjectURL) {
        objectUrlApi.revokeObjectURL(previewUrl);
    }
};

class NatureTrailImage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            previewUrl: null,
            selectedFileName: null,
            isCompressing: false
        };

        this.handleFileChange = this.handleFileChange.bind(this);
    }

    componentWillUnmount() {
        const { previewUrl } = this.state;

        revokePreviewUrl(previewUrl);
    }

    async handleFileChange(event) {
        const selectedFile = event.target.files && event.target.files[0];
        const objectUrlApi = getObjectUrlApi();
        const { previewUrl } = this.state;
        const { onSelectionChange } = this.props;

        revokePreviewUrl(previewUrl);

        if (!selectedFile) {
            this.setState({
                previewUrl: null,
                selectedFileName: null
            });

            if (onSelectionChange) {
                onSelectionChange(false);
            }

            return;
        }

        this.setState({ isCompressing: true });

        if (onSelectionChange) {
            onSelectionChange(false);
        }

        const compressedImage = await imageCompression(selectedFile, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true
        });
        const compressedFile = new File([compressedImage], selectedFile.name, { type: compressedImage.type });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(compressedFile);
        document.getElementById(this.props.id).files = dataTransfer.files;

        this.setState({
            previewUrl: objectUrlApi && objectUrlApi.createObjectURL ?
                objectUrlApi.createObjectURL(compressedFile) :
                null,
            selectedFileName: selectedFile.name,
            isCompressing: false
        });

        if (onSelectionChange) {
            onSelectionChange(true);
        }
    }

    render() {
        const {
            id, isDisabled, isRequired, name
        } = this.props;
        const { previewUrl, selectedFileName, isCompressing } = this.state;
        const hasSelectedFile = selectedFileName !== null;
        const wrapperClassNames = ['image-upload'];

        if (hasSelectedFile) {
            wrapperClassNames.push('image-upload--selected');
        }

        if (isDisabled) {
            wrapperClassNames.push('image-upload--disabled');
        }

        const fileSelectionLabel = hasSelectedFile ? 'Change photo' : 'Choose a photo';
        const imageUploadButtonLabel = isCompressing ? 'Please wait...' : fileSelectionLabel;

        return (
            <div className={wrapperClassNames.join(' ')}>
                <input type="file"
                    id={id}
                    disabled={isDisabled}
                    required={isRequired}
                    name={name}
                    accept=".jpg,.jpeg,.png"
                    onChange={this.handleFileChange}
                    style={{
                        opacity: 0, position: 'absolute', pointerEvents: 'none', width: 0, height: 0
                    }}
                    tabIndex={-1}/>

                <label
                    aria-disabled={isDisabled}
                    htmlFor={isCompressing ? undefined : id}
                    className="image-upload__dropzone">
                    {previewUrl ? (
                        <img
                            alt={`Preview of ${selectedFileName}`}
                            className="image-upload__preview"
                            src={previewUrl} />
                    ) : (
                        <span aria-hidden="true" className="image-upload__icon"><FontAwesomeIcon size='4x' width={48} height={48} icon={faCamera} /></span>
                    )}

                    {hasSelectedFile && (
                        <p aria-live="polite" className="image-upload__filename">
                            Selected: <strong>{selectedFileName}</strong>
                        </p>
                    )}

                    <span className="image-upload__button">
                        { imageUploadButtonLabel }
                    </span>

                    <p className="image-upload__hint">Accepted formats: JPG, PNG (Max. 10MB)</p>
                    <p className="image-upload__terms">By uploading, you confirm you own the rights to this image and <a className='link' href="#">agree to our terms below</a>.</p>
                </label>
            </div>
        );
    }
}

NatureTrailImage.propTypes = {
    id: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onSelectionChange: PropTypes.func
};

NatureTrailImage.defaultProps = {
    isDisabled: false,
    isRequired: false,
    onSelectionChange: null
};

class NatureTrailImageComponent extends BaseComponent {
    constructor(...args) {
        super(...args);

        this.setPhotoSubmitVisibility = this.setPhotoSubmitVisibility.bind(this);
        this.render();
        this.scrollToOnMobile();
    }

    scrollToOnMobile() {
        const { isDisabled } = this.options;

        if (toBoolean(isDisabled) || window.innerWidth > 768) {
            return;
        }

        let scrollTarget = this.element;

        while (scrollTarget && (!scrollTarget.classList || !scrollTarget.classList.contains('form-page__right'))) {
            scrollTarget = scrollTarget.parentElement;
        }

        if (scrollTarget) {
            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    setPhotoSubmitVisibility(isVisible) {
        let componentRoot = this.element;

        while (componentRoot &&
            (!componentRoot.classList || !componentRoot.classList.contains('nature-trail-wrapper'))) {
            componentRoot = componentRoot.parentElement;
        }

        if (!componentRoot) {
            return;
        }

        const photoSubmitButton = componentRoot.querySelector('.nature-trail-photo-submit');

        if (photoSubmitButton) {
            photoSubmitButton.hidden = !isVisible;
        }
    }

    render() {
        const {
            id, name, isDisabled, isRequired
        } = this.options;

        reactDOM.render(
            <NatureTrailImage
                id={id}
                isDisabled={toBoolean(isDisabled)}
                isRequired={toBoolean(isRequired)}
                name={name}
                onSelectionChange={this.setPhotoSubmitVisibility} />,
            this.element,
        );
    }

    onDestroy() {
        reactDOM.unmountComponentAtNode(this.element);
    }
}

export default NatureTrailImageComponent;
